import { useEffect, useRef, useState, type ReactNode } from "react";
import { useConnection } from "wagmi";
import type { Instrument, TicketPreview } from "../../sdk";
import { Button } from "../../components/ui";
import { ConnectButton } from "../../wallet/ConnectButton";
import { useAccount, useStreamStatus } from "../../state/account";
import { enableTrading, openPosition, useInvalidateKey, useKeyState } from "../../state/actions";
import { useMark } from "../../state/market";
import { openFunds } from "../../state/sheets";
import { useStore } from "../../state/store";
import { REFUSAL } from "../../lib/errors";
import { big, fmt, fromRaw, pctPpm, toRaw, usd } from "../../lib/num";
import { STAKE_STEPS, leverageFor, leverageSteps, rememberLeverage, rememberStake, setSlip, slip, type Side } from "./slip-state";
import type { CallView } from "./useCall";

const CENT = 10n ** 16n;
const DOLLAR = 10n ** 18n;
const WALLET_REFUSALS = new Set(["noWallet", "noAccount", "loading"]);

type Step = "connect" | "loading" | "enable" | "deposit" | null;

/** What stands between the player and a first call. */
export function useStep(): Step {
  const { status } = useConnection();
  const account = useAccount();
  const keyState = useKeyState();
  if (status !== "connected") return "connect";
  if (!account) return "loading";
  if (keyState.data !== "ready") return keyState.isLoading ? "loading" : "enable";
  const available = big(account.funds.availableBalance);
  const queued = big(account.funds.availableQueued);
  if (!account.hasDeposited && available === 0n && queued === 0n) return "deposit";
  return null;
}

/** Keeps the multiplier per market, within its cap. */
export function useMarketLeverage(instrument: Instrument) {
  useEffect(() => {
    setSlip({ leverage: leverageFor(instrument.symbol, instrument.maxLeverage) });
  }, [instrument.symbol, instrument.maxLeverage]);
}

function setLeverage(instrument: Instrument, leverage: number) {
  setSlip({ leverage });
  rememberLeverage(instrument.symbol, leverage);
}

const sideOf = (view: CallView, focus: Side) => (focus === "up" ? view.up : view.down);

/** Dollars without cents when there are none: `$10`, `$12.50`. */
const cash = (raw: bigint) => usd(raw, raw % (100n * CENT) === 0n ? { places: 0 } : {});

/* ---------- the two steppers ---------- */

/** A value between − and +, typed in place when tapped. */
function Stepper({
  label,
  text,
  unit,
  before,
  onText,
  onCommit,
  onDown,
  onUp,
  inputMode,
}: {
  label: string;
  text: string;
  unit: string;
  before: boolean;
  onText: (text: string) => void;
  onCommit?: () => void;
  onDown: (() => void) | null;
  onUp: (() => void) | null;
  inputMode: "decimal" | "numeric";
}) {
  const unitMark = <span className="st-unit">{unit}</span>;
  return (
    <div className="st" role="group" aria-label={label}>
      <button type="button" className="st-step" disabled={!onDown} onClick={() => onDown?.()} aria-label={`Lower ${label.toLowerCase()}`}>
        −
      </button>
      <label className="st-value">
        <span className="sr-only">{label}</span>
        {before && unitMark}
        <span className="st-auto">
          <span className="st-copy num" aria-hidden>
            {text || "0"}
          </span>
          <input
            className="num"
            inputMode={inputMode}
            autoComplete="off"
            spellCheck={false}
            placeholder="0"
            size={1}
            value={text}
            onChange={(event) => onText(event.target.value)}
            onBlur={() => onCommit?.()}
            onKeyDown={(event) => event.key === "Enter" && (event.target as HTMLInputElement).blur()}
          />
        </span>
        {!before && unitMark}
      </label>
      <button type="button" className="st-step" disabled={!onUp} onClick={() => onUp?.()} aria-label={`Raise ${label.toLowerCase()}`}>
        +
      </button>
    </div>
  );
}

function StakeStepper() {
  const stake = useStore(slip, (state) => state.stake);
  const dollars = Number((toRaw(stake, 18) ?? 0n) / DOLLAR);
  const lower = [...STAKE_STEPS].reverse().find((step) => step < dollars);
  const higher = STAKE_STEPS.find((step) => step > dollars);
  return (
    <Stepper
      label="Stake"
      text={stake}
      unit="$"
      before
      inputMode="decimal"
      onText={(value) => {
        const clean = value.replace(/,/g, ".").replace(/[^\d.]/g, "");
        const [whole = "", ...rest] = clean.split(".");
        setSlip({ stake: rest.length ? `${whole}.${rest.join("").slice(0, 2)}` : whole });
      }}
      onDown={lower === undefined ? null : () => setSlip({ stake: String(lower) })}
      onUp={higher === undefined ? null : () => setSlip({ stake: String(higher) })}
    />
  );
}

function LeverageStepper({ instrument }: { instrument: Instrument }) {
  const leverage = useStore(slip, (state) => state.leverage);
  const [text, setText] = useState(String(leverage));
  useEffect(() => setText(String(leverage)), [leverage]);
  const steps = leverageSteps(instrument.maxLeverage);
  const lower = [...steps].reverse().find((step) => step < leverage);
  const higher = steps.find((step) => step > leverage);
  return (
    <Stepper
      label="Multiplier"
      text={text}
      unit="×"
      before={false}
      inputMode="numeric"
      onText={(value) => setText(value.replace(/\D/g, "").slice(0, 4))}
      onCommit={() => {
        const n = Number.parseInt(text, 10);
        if (Number.isFinite(n) && n > 0) setLeverage(instrument, Math.max(1, Math.min(instrument.maxLeverage, n)));
        else setText(String(leverage));
      }}
      onDown={lower === undefined ? null : () => setLeverage(instrument, lower)}
      onUp={higher === undefined ? null : () => setLeverage(instrument, higher)}
    />
  );
}

/* ---------- what it pays ---------- */

/** The call in two numbers: what a move its way pays, and what the same move against loses. */
function Outcome({ instrument, view }: { instrument: Instrument; view: CallView }) {
  const { stake, focus } = useStore(slip);
  const side = sideOf(view, focus);
  const refusal = side.refusal && !WALLET_REFUSALS.has(side.refusal) ? side.refusal : null;

  if (refusal === "belowMinimumNotional") {
    const stakeRaw = toRaw(stake, 18) ?? 0n;
    const minimum = big(side.minimumNotional);
    const lowest = stakeRaw > 0n && minimum > 0n ? Number((minimum + stakeRaw - 1n) / stakeRaw) : null;
    return lowest !== null && lowest <= instrument.maxLeverage ? (
      <button type="button" className="oc oc-fix" onClick={() => setLeverage(instrument, lowest)}>
        At {cash(stakeRaw)}, play {lowest}× or more <span className="oc-go">Use {lowest}×</span>
      </button>
    ) : (
      <p className="oc oc-note">Raise your stake to play this multiplier</p>
    );
  }
  if (refusal === "insufficientFunds") return <p className="oc oc-note">More than your bankroll</p>;
  if (refusal === "noMargin" || refusal === "badAmount") return <p className="oc oc-note">Pick a stake</p>;
  if (refusal) return <p className="oc oc-note">{REFUSAL[refusal]}</p>;

  const scenario = side.scenarios?.[0];
  const margin = big(side.margin);
  const win = scenario ? big(scenario.payoutPnl) : null;
  const move = scenario ? `${fmt(fromRaw(BigInt(Math.abs(scenario.moveBp)), 2), { places: 2 })}%` : "—";
  const against = side.bustPpm ? pctPpm(side.bustPpm) : "—";
  const way = focus === "up" ? "▲" : "▼";
  const back = focus === "up" ? "▼" : "▲";
  return (
    <p className="oc" aria-live="polite">
      <span className="oc-half">
        <span className="oc-move">
          {way} {move}
        </span>
        <span className="oc-fig num up">+{win !== null && win > 0n ? usd(win) : "—"}</span>
      </span>
      <span className="oc-half">
        <span className="oc-move">
          {back} {against}
        </span>
        <span className="oc-fig num down">−{margin > 0n ? cash(margin) : "—"}</span>
      </span>
    </p>
  );
}

/** Stake from winnings waiting in the payout queue, when there are any. */
function Queued({ view }: { view: CallView }) {
  if (view.queued === 0n) return null;
  return (
    <label className="cc-queued">
      <input type="checkbox" checked={view.useDebt} onChange={(event) => setSlip({ useDebt: event.target.checked })} />
      <span>Use queued wins ({usd(view.queued)})</span>
    </label>
  );
}

/* ---------- the call ---------- */

function SetupStep({ step, size }: { step: Exclude<Step, null>; size: "md" | "lg" }) {
  const stream = useStreamStatus();
  const invalidateKey = useInvalidateKey();
  const [busy, setBusy] = useState(false);
  let action: ReactNode;
  let caption: string | null = null;
  switch (step) {
    case "connect":
      action = <ConnectButton size={size} block label="Connect wallet to play" />;
      break;
    case "loading":
      action = (
        <Button size={size} block disabled busy={stream !== "live"}>
          Loading your bankroll
        </Button>
      );
      break;
    case "enable":
      caption = "One signature. No gas.";
      action = (
        <Button
          tone="primary"
          size={size}
          block
          busy={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await enableTrading();
            } catch {
              // told by a toast
            } finally {
              setBusy(false);
              void invalidateKey();
            }
          }}
        >
          Turn on one-tap play
        </Button>
      );
      break;
    case "deposit":
      action = (
        <Button tone="primary" size={size} block onClick={() => openFunds("deposit")}>
          Deposit to play
        </Button>
      );
      break;
  }
  return (
    <div className="call-step">
      {action}
      {caption && <p className="call-step-words">{caption}</p>}
    </div>
  );
}

function CallButton({ side, preview, busy, placed, crowd, onCall }: { side: Side; preview: TicketPreview; busy: boolean; placed: boolean; crowd: number | null; onCall: () => void }) {
  const refusal = preview.refusal && !WALLET_REFUSALS.has(preview.refusal) ? preview.refusal : null;
  const full = refusal === "openInterest" || refusal === "aboveMaximumPosition" ? REFUSAL[refusal] : null;
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <button
      ref={ref}
      type="button"
      className={`call-btn call-${side} ${placed ? "is-placed" : ""}`}
      disabled={busy || refusal !== null}
      aria-busy={busy || undefined}
      onPointerEnter={() => setSlip({ aim: side, focus: side })}
      onPointerLeave={() => setSlip({ aim: null })}
      onFocus={() => setSlip({ aim: side, focus: side })}
      onBlur={() => setSlip({ aim: null })}
      onClick={() => {
        ref.current?.animate([{ transform: "scale(1)" }, { transform: "scale(0.96)" }, { transform: "scale(1)" }], {
          duration: 260,
          easing: "cubic-bezier(0.2, 0, 0, 1)",
        });
        onCall();
      }}
    >
      <span className="call-arrow" aria-hidden>
        {busy ? <span className="spinner" /> : side === "up" ? "▲" : "▼"}
      </span>
      <span className="call-words">
        <span className="call-word">{placed ? "Placed" : side === "up" ? "Up" : "Down"}</span>
        {full ? <span className="call-sub">{full}</span> : crowd !== null && !placed && <span className="call-sub num">{crowd}% of players</span>}
      </span>
    </button>
  );
}

/** The share of open money on each side, in whole percent. */
function crowdOf(instrument: Instrument): { up: number; down: number } | null {
  const long = big(instrument.longOi);
  const total = long + big(instrument.shortOi);
  if (total === 0n) return null;
  const up = Number((long * 100n + total / 2n) / total);
  return { up, down: 100 - up };
}

/** UP and DOWN: each places the call as it stands, in one tap once play is set up. Until then,
 * the next setup step stands in their place. */
export function CallButtons({ instrument, view, size = "md" }: { instrument: Instrument; view: CallView; size?: "md" | "lg" }) {
  const step = useStep();
  const mark = useMark(instrument.id);
  const state = useStore(slip);
  const [busy, setBusy] = useState<Side | null>(null);
  const [placed, setPlaced] = useState<Side | null>(null);
  const sending = useRef(false);

  useEffect(() => {
    if (!placed) return;
    const timer = setTimeout(() => setPlaced(null), 1_400);
    return () => clearTimeout(timer);
  }, [placed]);

  if (step !== null) return <SetupStep step={step} size={size} />;
  // a bankroll short of the stake: topping it up is the one thing to do
  if (view.up.refusal === "insufficientFunds" && view.down.refusal === "insufficientFunds") {
    return (
      <div className="call-step">
        <Button tone="primary" size={size} block onClick={() => openFunds("deposit")}>
          Deposit
        </Button>
      </div>
    );
  }

  const call = async (side: Side) => {
    const preview = side === "up" ? view.up : view.down;
    if (sending.current || preview.refusal || !instrument.openable) return;
    sending.current = true;
    setBusy(side);
    const label = `Call ${side === "up" ? "▲ Up" : "▼ Down"} ${instrument.symbol} ${state.leverage}× · ${usd(preview.margin ?? "0")}`;
    try {
      await openPosition(
        { instrumentId: instrument.id, isLong: side === "up", margin: state.stake, leverage: state.leverage, useDebt: view.useDebt, mark: mark?.raw ?? null },
        label,
      );
      rememberStake(state.stake);
      setPlaced(side);
      if ("vibrate" in navigator) navigator.vibrate(12);
    } catch {
      // told by a toast
    } finally {
      sending.current = false;
      setBusy(null);
    }
  };

  const crowd = crowdOf(instrument);
  return (
    <div className="call-row">
      <CallButton side="up" preview={view.up} busy={busy === "up"} placed={placed === "up"} crowd={crowd?.up ?? null} onCall={() => void call("up")} />
      <CallButton side="down" preview={view.down} busy={busy === "down"} placed={placed === "down"} crowd={crowd?.down ?? null} onCall={() => void call("down")} />
    </div>
  );
}

/** The call: stake and multiplier, what it pays, and UP and DOWN. Laid out in a card on the desk
 * and in the dock on a phone. */
export function CallCard({ instrument, view, size = "md" }: { instrument: Instrument; view: CallView; size?: "md" | "lg" }) {
  return (
    <div className="cc">
      <div className="cc-steppers">
        <StakeStepper />
        <LeverageStepper instrument={instrument} />
      </div>
      <Outcome instrument={instrument} view={view} />
      <Queued view={view} />
      <CallButtons instrument={instrument} view={view} size={size} />
    </div>
  );
}

/** `[` and `]` step the multiplier. */
export function useSlipKeys(instrument: Instrument) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const steps = leverageSteps(instrument.maxLeverage);
      const current = slip.get().leverage;
      if (event.key === "[") {
        const lower = [...steps].reverse().find((step) => step < current);
        if (lower) setLeverage(instrument, lower);
      } else if (event.key === "]") {
        const higher = steps.find((step) => step > current);
        if (higher) setLeverage(instrument, higher);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [instrument]);
}
